// --------------------------------------------------------------
// Created On: 2022-09-14
// Author: Zachary Thomas
//
// Last Modified: 2025-10-27
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import { LOCAL_STORAGE_AXIS_MODE_KEY } from "src/constants/localStorageVariables";
import React, { Fragment, useMemo } from "react";
import styles from "./DataHistoryControls.module.scss";

// Controls for changing data history settings.
export default function DataHistoryControls(props: Props): Component {
  const columnSizeStyle = useMemo(() => getColumnSizeSettings(props.allowAxisControls), [props.allowAxisControls]);

  // Gets the column size settings for specific controls based on which controls are enabled.
  function getColumnSizeSettings(allowAxisControls: boolean): string {
    if (allowAxisControls) {
      return "col-6 col-md-3";
    } else {
      return "col-6 col-md-4";
    }
  }

  // Stores axis settings to local storage and bubble up the axisMode value to the parent component.
  function handleChangeAxisMode(axisMode: AxisMode): void {
    localStorage.setItem(LOCAL_STORAGE_AXIS_MODE_KEY, axisMode);
    props.onChangeAxisMode(axisMode);
  }

  return (
    <div className={`${styles.body} row mx-1`}>
      <div className={columnSizeStyle}>
        <label>Start Date</label>
        <input
          className="form-control"
          type="date"
          value={props.startDate}
          onChange={(e) => props.onChangeStartDate(e.target.value)}
        />
      </div>

      <div className={columnSizeStyle}>
        <label>End Date</label>
        <input
          className="form-control"
          type="date"
          value={props.endDate}
          onChange={(e) => props.onChangeEndDate(e.target.value)}
        />
      </div>

      <div className={columnSizeStyle}>
        <label>Mode</label>
        <select
          className="form-select"
          value={props.displayMode}
          onChange={(e) => props.onChangeDisplayMode(e.target.value as DisplayMode)}
        >
          {props.allowLogs && (
            <Fragment>
              <option value="CHANGE_LOGS">Change Logs</option>
              <option value="ALL_LOGS">All Logs</option>
            </Fragment>
          )}
          {props.allowStandardLine && <option value="STANDARD_LINE_CHART">Standard Line Chart</option>}
          {props.allowMovingAverageLine && <option value="MOVING_LINE_CHART">Moving Average Line Chart</option>}
          {props.allowAccumulation && (
            <Fragment>
              <option value="HOURLY_ACCUMULATION">Hourly Accumulation</option>
              <option value="DAILY_ACCUMULATION">Daily Accumulation</option>
              <option value="WEEKLY_ACCUMULATION">Weekly Accumulation</option>
              <option value="MONTHLY_ACCUMULATION">Monthly Accumulation</option>
            </Fragment>
          )}
        </select>
      </div>

      {props.allowAxisControls && (
        <div className={columnSizeStyle}>
          <label>Axis Labels</label>
          <select
            className="form-select"
            value={props.axisMode}
            onChange={(e) => handleChangeAxisMode(e.target.value as AxisMode)}
          >
            <option value="Y_AXIS_UOM">Y-axis Per Unit Of Measure</option>
            <option value="Y_AXIS_ATTRIBUTE">Y-axis Per Attribute</option>
          </select>
        </div>
      )}
    </div>
  );
}

interface Props {
  startDate: string;
  endDate: string;
  displayMode: DisplayMode;
  axisMode: AxisMode;
  allowLogs: boolean;
  allowAccumulation: boolean;
  allowStandardLine: boolean;
  allowMovingAverageLine: boolean;
  allowAxisControls: boolean;
  onChangeStartDate: (startDate: string) => void;
  onChangeEndDate: (endDate: string) => void;
  onChangeDisplayMode: (mode: DisplayMode) => void;
  onChangeAxisMode: (mode: AxisMode) => void;
}

type DisplayMode =
  | "STANDARD_LINE_CHART"
  | "MOVING_LINE_CHART"
  | "HOURLY_ACCUMULATION"
  | "DAILY_ACCUMULATION"
  | "WEEKLY_ACCUMULATION"
  | "MONTHLY_ACCUMULATION"
  | "CHANGE_LOGS"
  | "ALL_LOGS";

type AxisMode = "Y_AXIS_ATTRIBUTE" | "Y_AXIS_UOM";
